import { treeTypes } from "../../constants/treeState";
import { constructHierarchy } from "../../utilities/HierarchyOperations/constructHierarchy";
import { useStructuralHierarchyQuery } from "../../utilities/queries/useStructuralHierachyQuery";
import { useState, useEffect, useMemo, useRef } from "react";
import StructuralHierarchyTree from "../StructuralHierarchyTree/StructuralHierarchyTree";
import MiniTreeNode from "../StructuralHierarchyTree/MiniTreeNode";
import TreeBulkActionButton from "./TreeBulkActionButton";
import { getAutoSelectedAndDisabledNodes } from "../../utilities/HierarchyOperations/getAutoSelectedAndDisabledNodes";
import Spinner from "../UISpinner/UISpinner";
import { getDescendants } from "../../utilities/HierarchyOperations/getDescendants";


export default function RenderHierarchyTree({
	treeMaxHeight,
	treeButton,
	checkedItems,
	setCheckedItems,
	disableAllCheckboxes = false,
	selectAllTrigger = 0
}) {
	const { data, isLoading, error } = useStructuralHierarchyQuery();
	const [hierarchyData, setHierarchyData] = useState(null);
	

	const [expandedItems, setExpandedItems] = useState([]);
	
	const allNodeIds = useMemo(() => {
		if (!hierarchyData) return [];
		// keeps numeric keys as strings; filters out "root"
		return Object.keys(hierarchyData).filter(k => Number.isFinite(Number(k)));
	}, [hierarchyData]);

	const lastAppliedTriggerRef = useRef(0);
	useEffect(() => {
		// parent reset (modal close) — clear our internal memory too
		if (selectAllTrigger === 0) {
			lastAppliedTriggerRef.current = 0;
		}
	}, [selectAllTrigger]);


	useEffect(() => {
		// Only run for new triggers
		if (selectAllTrigger <= lastAppliedTriggerRef.current) return;

		if (!disableAllCheckboxes) return;

		// Wait for async tree data
		if (!hierarchyData) return;

		setCheckedItems(allNodeIds); // all ids (strings)
		lastAppliedTriggerRef.current = selectAllTrigger;
	}, [
		selectAllTrigger,
		disableAllCheckboxes,
		hierarchyData,
		allNodeIds,
		setCheckedItems,
	]);



	useEffect(() => {
		if (data) {
			const builtHierarchy = constructHierarchy(data);
			setHierarchyData(builtHierarchy);

			const rootIndex = builtHierarchy?.root?.children[0];
			if (
				rootIndex &&
				builtHierarchy[rootIndex]?.children?.length > 0
			) {
				setExpandedItems([rootIndex]); 
			}
		}
	}, [data]);

	const disabledItems = useMemo(() => {
		if (!hierarchyData) return [];

		if (disableAllCheckboxes) {
			return allNodeIds; // disable everything in PortalAdmin
		}

		const { disabled } = getAutoSelectedAndDisabledNodes(hierarchyData, checkedItems);
		return [...new Set(disabled)];
	}, [disableAllCheckboxes, hierarchyData, allNodeIds, checkedItems]);



	const setSelfAndNextLayerAsUnchecked = (accountId) => {
		// uncheck clicked checkbox
		setCheckedItems([...checkedItems.filter((i) => i !== accountId)]);
		
	};

	const setSelfAndDescendantsAsChecked = (accountId) => {
		const descendantIds = getDescendants(accountId, hierarchyData, true);

		// existing checked items + checked item + checked item's descendants
		setCheckedItems([
			...checkedItems,
			accountId,
			...descendantIds.filter((i) => !checkedItems.some(s => s === i))]);
	};
	const handleCheckboxClick = (node) => {
		const isChecked = node.context.viewStateFlags.checkedItems;
		const accountId = node.item.accountId;

		if (isChecked) {
			setSelfAndNextLayerAsUnchecked(accountId);
		} else {
			setSelfAndDescendantsAsChecked(accountId);
		}
	};
	const handleRenderNode = (node) => {

		return <MiniTreeNode node={node}
			treeSize={treeTypes.Mini}
			showActionButtons={false}
			handleCheckboxClick={handleCheckboxClick}
		/>;
	};

	const getNodeLabel = (item) => {
		return `${item.data.name} (${item.data.accountId})`;
	};
	const handleExpand = (item) => {
		setExpandedItems([...expandedItems, item.index]);
	};
	const handleCollapse = (item) => {
		setExpandedItems(
			expandedItems.filter(
				(expandedItemIndex) => expandedItemIndex !== item.index
			)
		);
	};
	const selectAll = () => {
		const parentId = hierarchyData.root.children[0];
		const parentDescendants = getDescendants(parentId, hierarchyData, false);

		//can't use setSelfAndDescendantsAsChecked becuase it would create a race condition with setState
		let childDescendants = [];
		for (const child of parentDescendants) {
			childDescendants = [...childDescendants, ...getDescendants(child, hierarchyData, true)];
		}

		setCheckedItems([...childDescendants, ...parentDescendants]);
	};

	const deselectAll = () => {
		setCheckedItems([]);
	};
	const treeActionButtons = {
		selectAll: {
			text: "Select All",
			actionHandler: selectAll
		},
		deselectAll: {
			text: "Deselect All",
			actionHandler: deselectAll
		}
	};

	const treeProps = {
		hierarchyData,
		handleRenderNode,
		getNodeLabel,
		handleExpand,
		handleCollapse,
		expandedItems,
		disabledItems,
		checkedItems
	};
	if (isLoading) {
		return <Spinner text="Loading tree..." />
	}
	const actionButton =
		!disableAllCheckboxes && treeActionButtons[treeButton] ? (
			<TreeBulkActionButton
				actionHandler={treeActionButtons[treeButton].actionHandler}
				text={treeActionButtons[treeButton].text}
			/>
		) : null;

	return (
		<>
			{hierarchyData && <StructuralHierarchyTree {...treeProps}
				treeId="main-tree"
				treeSize={treeTypes.Mini}
				treeMaxHeight={treeMaxHeight}
				renderDepthOffset={30}
				actionButton={actionButton} 
			/>}
		</>
	)
}